/**
 * EditTaskModal Component
 * Modal for editing existing tasks
 * Handles task updates and form validation
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  TaskAlt,
  AttachFile,
  Assignment,
  Person,
  Schedule,
  Close,
  Add,
  Edit,
  Delete,
  Check,
  Clear
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTicketDropdowns, fetchProjectDropdownsByUser, createTicketType, createTicketPriority, updateTicketDropdown, deleteTicketDropdown, searchTickets } from '../../store';
import { isStorageQuotaExceededError, uploadFile } from '../../store/fileSlice';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { COLORS } from '../../shared/colors';
import { normalizePastedText, normalizeRichTextHtml, prepareContentForEditing } from '../../shared/htmlUtils';
import { minutesToTimeString, timeStringToMinutes, isValidTimeFormat, normalizeTimeString, validateTimeDigitLimits, buildDateTimeString, getLocalDatePart, getLocalTimePart } from '../../shared/timeUtils';
import { TASK_TYPE, TASK_STATUS, TASK_PRIORITY, CHARACTER_LIMITS, TOAST_MESSAGES, USER_ROLES, PROJECT_TYPES, RESTRICTED_NUMBER_KEYS } from '../../shared/appConstants';
import { REGEX } from '../../shared/regex';
import { clearDraftFields } from '../../shared/autoSaveUtils';
import { getEntityId, normalizeComparableValue, normalizeDateValue, normalizeIdList, stableStringify } from '../../shared/formNormalizationUtils';
import { buildLimitedPasteValue, clampTextToLimit, stringToColor, getPriorityColor } from '../../shared/commonFunctions';
import { filterEmbeddedAttachments, isAttachmentAlreadyEmbedded, buildCanonicalInlineImageUrl } from '../../shared/attachmentUtils';
import { getDefaultPriorityId, getUnassignedPriority } from '../../shared/priorityUtils';
import CommentBox from './CommentBox';
import DropdownSelector from './DropdownSelector';
import ChecklistManager from './ChecklistManager';
import OverflowTooltip from '../../shared/OverflowTooltip';
import AssigneeSelector from '../../shared/AssigneeSelector';
import CustomDateRangePicker from '../../shared/CustomDateRangePicker';
import CustomColorPopover from '../shared/CustomColorPopover';
import './TaskModal.scss';
import { isRecurrenceComplete, normalizeRecurrenceConfig } from '../../shared/recurrenceUtils';
import RecurrencePanel from './RecurrencePanel';
import { api, ENDPOINTS } from '../../services';
import { mergeChecklistChange } from '../../shared/checklistMergeUtils';

const normalizeChecklistForComparison = (value) => {
  if (value && !Array.isArray(value) && Array.isArray(value.checklists)) {
    return normalizeComparableValue({ checklists: value.checklists });
  }

  if (!Array.isArray(value) || value.length === 0) {
    return { checklists: [] };
  }

  if (value[0] && Object.prototype.hasOwnProperty.call(value[0], 'checklists')) {
    return normalizeComparableValue(value[0]);
  }

  return normalizeComparableValue({ checklists: value });
};

const normalizeFormForComparison = (data) => ({
  title: data.title || '',
  taskType: String(getEntityId(data.taskType)),
  priority: String(getEntityId(data.priority)),
  status: String(getEntityId(data.status)),
  assignees: normalizeIdList(data.assignees),
  project: String(getEntityId(data.project)),
  dueDate: normalizeDateValue(data.dueDate),
  startDate: normalizeDateValue(data.startDate),
  description: normalizeRichTextHtml(data.description || ''),
  attachments: (data.attachments || []).map((file) => ({
    key: file?.key || '',
    url: file?.url || ''
  })),
  labels: normalizeIdList(data.labels),
  sprint: String(getEntityId(data.sprint)),
  acceptanceCriteria: normalizeRichTextHtml(data.acceptanceCriteria || ''),
  checklist: normalizeChecklistForComparison(data.checklist),
  estimateTime: isValidTimeFormat(data.estimateTime)
    ? String(timeStringToMinutes(data.estimateTime))
    : (data.estimateTime || ''),
  parent_ticket_id: String(getEntityId(data.parent_ticket_id)),
  is_recurring: Boolean(data.is_recurring),
  recurrence_config: normalizeRecurrenceConfig(data.recurrence_config)
});


const EditTaskModal = ({ open, onClose, onSubmit, taskData, selectedProjectId, userRole, isAddMode = false, preselectedStatusId, selectedDate, parentTicketId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdowns, dropdownsLoading, projectDropdownsByUser, projectDropdownsByUserLoading } = useSelector((state) => state.tickets);

  // Check if user can manage task types and priorities (edit/delete - only admin and pm)
  const canManageTaskTypes = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.PM;
  const canManagePriorities = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.PM;

  const [formData, setFormData] = useState({
    id: '',
    title: '',
    taskType: '',
    priority: '',
    status: '',
    assignees: [],
    project: '',
    dueDate: '',
    startDate: getLocalDatePart(new Date()),
    startTime: '',
    dueTime: '',
    description: '',
    attachments: [],
    labels: [],
    sprint: '',
    acceptanceCriteria: '',
    checklist: [],
    estimateTime: '',
    storyPoints: '',
    parent_ticket_id: '',
    is_recurring: false,
    recurrence_config: null,
    next_rep_on: null
  });
  
  const descriptionCommentBoxRef = useRef(null);
  const acceptanceCriteriaCommentBoxRef = useRef(null);
  const isInitializingFormRef = useRef(false);
  // Keep the user's checklist operations, rather than later treating the modal's
  // originally loaded checklist as the source of truth.
  const checklistChangesRef = useRef([]);
  
  const [originalData, setOriginalData] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentTicketSearchTerm, setParentTicketSearchTerm] = useState('');
  const [parentTicketOptions, setParentTicketOptions] = useState([{ id: '', isNone: true }]);
  const [isParentTicketLoading, setIsParentTicketLoading] = useState(false);

  const normalizeTicketOptions = (result) => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.rows)) return result.rows;
    if (Array.isArray(result?.tickets)) return result.tickets;
    return [];
  };

  const processedLabels = useMemo(
    () => Array.isArray(taskData?.labels)
          ? taskData.labels.map(label => typeof label === 'object' ? label.id : label)
          : [],
    [taskData?.labels]
  );

  // Gate reset to only fire when taskData.id changes, not on every Redux update
  const loadedTaskIdRef = React.useRef(null);

  useEffect(() => {
    if (!open) {
      loadedTaskIdRef.current = null;
      checklistChangesRef.current = [];
      setOriginalData({});
      setIsFormDirty(false);
      setStoryPointsError('');
      isInitializingFormRef.current = false;
    }
  }, [open]);

  // Fetch dropdown data when modal opens - only if not already loaded
  useEffect(() => {
    if (open) {
      // Only fetch project dropdowns if not already loaded
      if (!projectDropdownsByUser || projectDropdownsByUser.length === 0) {
        dispatch(fetchProjectDropdownsByUser());
      }
      
      // Only fetch ticket dropdowns if completely missing for any project
      const hasAnyDropdownData = dropdowns && (
        (dropdowns.types && dropdowns.types.length > 0) ||
        (dropdowns.priorities && dropdowns.priorities.length > 0) ||
        (dropdowns.statuses && dropdowns.statuses.length > 0)
      );
      const hasAssignees = dropdowns?.assignees && dropdowns.assignees.length > 0;
      
      const projectId = (typeof taskData?.project === 'object' ? taskData?.project?.id : taskData?.project) || selectedProjectId;
      
      // Fetch when missing data or switching projects
      if (projectId && (!hasAnyDropdownData || !hasAssignees || lastDropdownProjectIdRef.current !== projectId)) {
        dispatch(fetchTicketDropdowns(projectId));
        lastDropdownProjectIdRef.current = projectId;
      }
    }
  }, [open, dispatch, selectedProjectId, taskData?.project, projectDropdownsByUser, dropdowns]);

  const [dateError, setDateError] = useState(''); // eslint-disable-line no-unused-vars
  const [timeError, setTimeError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [storyPointsError, setStoryPointsError] = useState('');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const lastDropdownProjectIdRef = React.useRef(null);

  const mentionUsersList = React.useMemo(() => {
    if (dropdowns?.assignees && dropdowns.assignees.length > 0) return dropdowns.assignees;
    if (Array.isArray(taskData?.assignees) && taskData.assignees.length > 0) return taskData.assignees;
    if (Array.isArray(taskData?.users) && taskData.users.length > 0) return taskData.users;
    return [];
  }, [dropdowns?.assignees, taskData?.assignees, taskData?.users]);

  useEffect(() => {
    if (!open) {
      setDefaultsApplied(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && isAddMode) {
      isInitializingFormRef.current = true;
      setFormData({
        id: '',
        title: '',
        taskType: '',
        priority: getDefaultPriorityId(dropdowns?.priorities),
        status: '',
        assignees: [],
        project: selectedProjectId || '',
        dueDate: '',
        startDate: getLocalDatePart(new Date()),
        startTime: '',
        dueTime: '',
        description: '',
        attachments: [],
        labels: [],
        sprint: '',
        acceptanceCriteria: '',
        checklist: [],
        estimateTime: '',
        storyPoints: '',
        parent_ticket_id: parentTicketId || '',
        is_recurring: false,
        recurrence_config: null,
        next_rep_on: null
      });
      setOriginalData({});
      setIsFormDirty(false);
      checklistChangesRef.current = [];
      isInitializingFormRef.current = false;
    }
  }, [open, isAddMode, selectedProjectId, parentTicketId]);

  useEffect(() => {
    if (!open || defaultsApplied || !dropdowns || dropdownsLoading || projectDropdownsByUserLoading || !isAddMode) return;
    const data = dropdowns || {};
    if (data.types?.length > 0 || data.priorities?.length > 0 || data.statuses?.length > 0 || projectDropdownsByUser?.length > 0 || data.assignees?.length > 0) {
      setFormData((prev) => {
        const next = { ...prev };
        let changed = false;
        if (!next.taskType && (data.types?.length || 0) > 0) { 
          const taskType = data.types.find(type => type.name === TASK_TYPE.TASK);
          next.taskType = taskType ? taskType.id : data.types[0].id; 
          changed = true; 
        }
        if (!next.priority && (data.priorities?.length || 0) > 0) { 
          const unassignedPriority = getUnassignedPriority(data.priorities);
          if (unassignedPriority) {
            next.priority = unassignedPriority.id; 
            changed = true; 
          }
        }
        if (!next.status && (data.statuses?.length || 0) > 0) { 
          if (preselectedStatusId) {
            next.status = preselectedStatusId;
          } else {
            const todoStatus = data.statuses.find(s => s.name === TASK_STATUS.TO_DO);
            next.status = todoStatus ? todoStatus.id : data.statuses[0].id;
          }
          changed = true; 
        }
        if (!next.project) {
          if (selectedProjectId) {
            next.project = selectedProjectId;
            changed = true;
          } else if ((projectDropdownsByUser?.length || 0) > 0) {
            next.project = projectDropdownsByUser[0].id;
            changed = true;
          }
        }
        if ((!next.assignees || next.assignees.length === 0) && (data.assignees?.length || 0) > 0) { next.assignees = [data.assignees[0].id]; changed = true; }
        if (!next.startDate && selectedDate) { next.startDate = selectedDate; changed = true; }
        return changed ? next : prev;
      });
      setDefaultsApplied(true);
    }
  }, [open, defaultsApplied, dropdowns, dropdownsLoading, projectDropdownsByUser, projectDropdownsByUserLoading, selectedProjectId, preselectedStatusId, selectedDate, isAddMode]);

  useEffect(() => {
    if (taskData && open && loadedTaskIdRef.current !== taskData.id && !isAddMode) {
      loadedTaskIdRef.current = taskData.id;
      const estimateTimeValue = minutesToTimeString(taskData.estimate_time_in_minutes || taskData.estimateTimeInMinutes);
      
      let sprintId = '';
      if (taskData.sprint) {
        sprintId = typeof taskData.sprint === 'object' ? (taskData.sprint.id || '') : taskData.sprint;
      } else if (taskData.sprint_id) {
        sprintId = taskData.sprint_id;
      }

      // Resolve assignees: prefer assignees array, fall back to single assignee
      let assigneeIds = [];
      if (Array.isArray(taskData.assignees) && taskData.assignees.length > 0) {
        assigneeIds = taskData.assignees.map(a => typeof a === 'object' ? a.id : a);
      } else if (taskData.assignee) {
        assigneeIds = [taskData.assignee];
      }
      
        const startDateValue = taskData.startDate || getLocalDatePart(taskData.start_date);
        const dueDateValue = taskData.dueDate || getLocalDatePart(taskData.end_date);
        const startTimeValue = taskData.startTime || getLocalTimePart(taskData.start_date);
        // Preserve intentional 12:00 AM only when the ticket actually has a configured due time.
        const dueTimeValue = taskData.has_due_time || taskData.hasDueTime
          ? (taskData.dueTime || getLocalTimePart(taskData.end_date, { includeMidnight: true }))
          : '';
        
        const data = {
          id: taskData.id || '',
          title: taskData.title || '',
          taskType: taskData.taskType || '',
          priority: taskData.priority?.id || taskData.priority || getDefaultPriorityId(dropdowns?.priorities) || '',
          status: taskData.status || '',
          assignees: assigneeIds,
          project: taskData.project || (selectedProjectId || ''),
          dueDate: dueDateValue,
          startDate: startDateValue,
          startTime: startTimeValue,
          dueTime: dueTimeValue,
        description: taskData.description || '',
        attachments: taskData.attachments || [],
        labels: processedLabels,
        sprint: sprintId,
        acceptanceCriteria: taskData.acceptanceCriteria || taskData.acceptance_criteria || '',
        checklist: taskData.checklist || [],
        estimateTime: estimateTimeValue,
        storyPoints: taskData.story_points || taskData.storyPoints || '',
        parent_ticket_id: taskData.parent_ticket_id || taskData.parent_ticket?.id || taskData.parentTicketId || '',
        is_recurring: Boolean(taskData.is_recurring),
        recurrence_config: taskData.is_recurring ? normalizeRecurrenceConfig(taskData.recurrence_config) : null,
        next_rep_on: taskData.next_rep_on || null
      };
      isInitializingFormRef.current = true;
      setFormData(data);
      setOriginalData(data);
      setIsFormDirty(false);
      setStoryPointsError('');
      checklistChangesRef.current = [];
      isInitializingFormRef.current = false;
    }
  }, [taskData, open, selectedProjectId, processedLabels, isAddMode]);

  useEffect(() => {
    if (!open || !isAddMode) return;
    setFormData((prev) => ({
      ...prev,
      parent_ticket_id: parentTicketId || ''
    }));
  }, [open, isAddMode, parentTicketId]);

  const activeProjectId = typeof formData.project === 'object' ? formData.project?.id : formData.project;

  const updateFormData = (updater, { trackDirty = true } = {}) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      if (trackDirty && !isAddMode && !isInitializingFormRef.current) {
        setIsFormDirty(
          stableStringify(normalizeFormForComparison(next)) !== stableStringify(normalizeFormForComparison(originalData))
        );
      }

      return next;
    });
  };

  useEffect(() => {
    if (!open || !activeProjectId) {
      setParentTicketOptions([{ id: '', isNone: true }]);
      setIsParentTicketLoading(false);
      return;
    }

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      try {
        setIsParentTicketLoading(true);
        const result = await dispatch(searchTickets({
          projectId: activeProjectId,
          searchTerm: parentTicketSearchTerm
        })).unwrap();
        const normalizedOptions = normalizeTicketOptions(result);
        const options = normalizedOptions.filter((ticket) => String(ticket.id) !== String(formData.id));
        if (isMounted) {
          setParentTicketOptions([{ id: '', isNone: true }, ...options]);
        }
      } catch (error) {
        if (isMounted) {
          setParentTicketOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsParentTicketLoading(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [open, activeProjectId, formData.id, parentTicketSearchTerm, dispatch]);

  const handleInputChange = (field, value) => {
    const nextValue = field === 'title'
      ? clampTextToLimit(value, CHARACTER_LIMITS.TICKET_TITLE)
      : value;

    setFormData(prev => ({
      ...prev,
      [field]: nextValue
    }));

    if (field === 'title') {
      validateTitle(nextValue);
      return;
    }

    if (field === 'storyPoints') {
      validateStoryPoints(nextValue);
    }

    // Validate dates and times when they change
    if (field === 'startDate' || field === 'dueDate' || field === 'startTime' || field === 'dueTime') {
      validateDates(
        field === 'startDate' ? nextValue : formData.startDate, 
        field === 'dueDate' ? nextValue : formData.dueDate,
        field === 'startTime' ? nextValue : formData.startTime,
        field === 'dueTime' ? nextValue : formData.dueTime
      );
    }
    
    // Validate time format when estimate time changes
    if (field === t('taskModal.estimateTime')) {
      validateTimeFormat(nextValue);
    }
  };

  const handleChecklistChange = (checklist, changeMeta = {}) => {
    handleInputChange('checklist', checklist);

    // ChecklistManager supplies this for every user action.  These operations
    // are replayed against a fresh server copy immediately before saving.
    if (changeMeta?.type) {
      checklistChangesRef.current.push(changeMeta);
    }
  };

  const handleTitlePaste = (event) => {
    const pastedText = normalizePastedText(event, { mode: 'title' });
    const { value } = buildLimitedPasteValue({
      currentValue: formData.title,
      pastedText,
      selectionStart: event.target.selectionStart,
      selectionEnd: event.target.selectionEnd,
      limit: CHARACTER_LIMITS.TICKET_TITLE
    });

    event.preventDefault();
    handleInputChange('title', value);
  };

  const handleRichTextChange = (field, content) => {
    if (isInitializingFormRef.current) return;

    updateFormData(prev => {
      if (prev[field] === content) return prev;
      return {
        ...prev,
        [field]: content
      };
    });
  };

  const validateDates = (startDate, endDate, startTime = formData.startTime, dueTime = formData.dueTime) => {
    if (startDate && endDate) {
      const sDate = new Date(startDate);
      const eDate = new Date(endDate);
      sDate.setHours(0, 0, 0, 0);
      eDate.setHours(0, 0, 0, 0);
      
      if (sDate > eDate) {
        setDateError(t('taskModal.startDateAfterEndDate'));
        return false;
      } else if (sDate.getTime() === eDate.getTime()) {
        if (startTime && dueTime) {
          const [startH, startM] = startTime.split(':').map(Number);
          const [dueH, dueM] = dueTime.split(':').map(Number);
          if (dueH < startH || (dueH === startH && dueM < startM)) {
            setDateError(t('taskModal.dueTimeBeforeStartTime'));
            return false;
          }
        }
      }
    }
    setDateError('');
    return true;
  };

  const validateTimeFormat = (timeString) => {
    const digitError = validateTimeDigitLimits(timeString);
    if (digitError) {
      setTimeError(t('taskModal.numbersCannotExceed5Digits'));
      return false;
    }
    
    if (!isValidTimeFormat(timeString)) {
      setTimeError(t('taskModal.invalidEstimateFormat'));
      return false;
    }
    
    setTimeError('');
    return true;
  };

  const validateTitle = (title) => {
    if ((title || '').length > CHARACTER_LIMITS.TICKET_TITLE) {
      setTitleError(t('taskModal.titleCannotExceed255Chars', 'Task title cannot exceed 255 characters'));
      return false;
    }

    setTitleError('');
    return true;
  };

  const validateStoryPoints = (storyPointsValue = formData.storyPoints) => {
    const hasValue = storyPointsValue !== '' && storyPointsValue !== null;

    if (!hasValue) {
      setStoryPointsError('');
      return true;
    }

    const sp = Number(storyPointsValue);
    if (isNaN(sp) || sp < 1 || sp > 40) {
      setStoryPointsError(t('taskModal.storyPointsInvalid'));
      return false;
    }

    setStoryPointsError('');
    return true;
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const currentAttachmentsCount = formData.attachments?.length || 0;
    if (currentAttachmentsCount + files.length > CHARACTER_LIMITS.MAX_ATTACHMENTS) {
      toast.error(t('taskModal.maxAttachmentsError'));
      event.target.value = '';
      return;
    }
    
    setIsUploadingFiles(true);
    const uploadedFiles = [];
  
    try {
      for (const file of files) {
        try {
          const uploadedFile = await dispatch(uploadFile(file)).unwrap();
          uploadedFiles.push(uploadedFile);
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
          if (!isStorageQuotaExceededError(error)) {
            let errorMessage = t('taskModal.attachmentUploadError');
            if (typeof error === 'string' && error.trim() !== '') {
              errorMessage = error;
            } else if (error && typeof error === 'object' && (error.msg || error.message)) {
              errorMessage = error.msg || error.message;
            }
            toast.error(errorMessage);
          }
        }
      }
      
      if (uploadedFiles.length > 0) {
        updateFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, ...uploadedFiles]
        }));
        toast.success(t(uploadedFiles.length === 1 ? 'taskModal.attachmentUploadSuccess' : 'taskModal.attachmentsUploadSuccess'));
      }
    } finally {
      setIsUploadingFiles(false);
      event.target.value = '';
    }
  };

  const removeAttachment = (target) => {
    updateFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((item, i) => {
        if (typeof target === 'number') {
          return i !== target;
        }
        return item !== target && (!target?.key || item?.key !== target.key);
      })
    }));
  };

  const buildChecklistPayload = (checklistValue) => {
    if (checklistValue && !Array.isArray(checklistValue) && Array.isArray(checklistValue.checklists)) {
      return checklistValue;
    }

    if (!Array.isArray(checklistValue) || checklistValue.length === 0) {
      return { checklists: [] };
    }

    if (
      checklistValue[0] &&
      (Object.prototype.hasOwnProperty.call(checklistValue[0], 'checklists') ||
        (Object.prototype.hasOwnProperty.call(checklistValue[0], 'title') &&
          Object.prototype.hasOwnProperty.call(checklistValue[0], 'items')))
    ) {
      if (Object.prototype.hasOwnProperty.call(checklistValue[0], 'checklists')) {
        return checklistValue[0];
      }
      return { checklists: checklistValue };
    }

    const prioritiesByName = new Map((dropdowns.priorities || []).map((p) => [p.name, p.id]));
    const assigneesByName = new Map((dropdowns.assignees || []).map((a) => [a.name, a.id]));

    const checklistMap = new Map();
    checklistValue.forEach((item) => {
      const title = (item?.checklistTitle || 'Default Checklist').trim() || 'Default Checklist';
      if (!checklistMap.has(title)) {
        checklistMap.set(title, {
          id: item?.checklistId || crypto.randomUUID(),
          title,
          position: checklistMap.size + 1,
          items: []
        });
      }

      const group = checklistMap.get(title);
      const assigneeName = item?.assignedTo?.name || null;
      const priorityName = item?.priority?.name || null;

      group.items.push({
        id: item?.id || crypto.randomUUID(),
        text: item?.text || '',
        completed: Boolean(item?.completed),
        assignedTo: assigneeName ? (assigneesByName.get(assigneeName) || null) : null,
        dueDate: item?.dueDate || null,
        priority: priorityName ? (prioritiesByName.get(priorityName) || null) : null,
        position: group.items.length + 1
      });
    });

    return { checklists: Array.from(checklistMap.values()) };
  };

  const uploadPendingDescriptionAttachments = async (pendingFiles = descriptionCommentBoxRef.current?.getAttachments?.() || []) => {
    const editorEl = descriptionCommentBoxRef.current?.getEditorElement?.();
    const filesToUpload = [...pendingFiles];

    if (editorEl && typeof fetch !== 'undefined') {
      const blobImgs = editorEl.querySelectorAll('img[src^="blob:"]');
      for (const img of blobImgs) {
        const src = img.getAttribute('src');
        const alreadyTracked = filesToUpload.some(f => f._previewUrl === src);
        if (!alreadyTracked && src) {
          try {
            const res = await fetch(src);
            if (res.ok) {
              const blob = await res.blob();
              const ext = (blob.type || 'image/png').split('/')[1] || 'png';
              const file = new File([blob], img.alt || `screenshot-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
              file._previewUrl = src;
              file._inlineScreenshotId = img.getAttribute('data-screenshot-id') || img.closest('figure')?.getAttribute('data-screenshot-id');
              file._isInline = true;
              filesToUpload.push(file);
            }
          } catch (e) {
            // Blob URL expired or unreachable
          }
        }
      }
    }

    if (filesToUpload.length === 0) {
      return [];
    }

    const uploadedFiles = [];
    for (const file of filesToUpload) {
      // Description editor attachments are uploaded only with the final submit.
      if (file?.key && file?.url) {
        uploadedFiles.push({
          ...file,
          _isInline: Boolean(file._inlineScreenshotId || file._isInline || file._previewUrl)
        });
        continue;
      }

      try {
        const uploadedFile = await dispatch(uploadFile(file)).unwrap();
        const uploadedRecord = {
          ...uploadedFile,
          _inlineScreenshotId: file._inlineScreenshotId,
          _previewUrl: file._previewUrl,
          _isInline: Boolean(file._inlineScreenshotId || file._isInline || file._previewUrl)
        };
        uploadedFiles.push(uploadedRecord);
        if (file._inlineScreenshotId || file._previewUrl) {
          descriptionCommentBoxRef.current?.resolveUploadedScreenshotUrl?.(
            file._inlineScreenshotId || file._previewUrl,
            uploadedFile.url,
            uploadedFile.key
          );
        }
      } catch (error) {
        if (!isStorageQuotaExceededError(error)) {
          let errorMessage = t('taskModal.descriptionAttachmentUploadError', 'Failed to upload description attachment. Please try again.');
          if (typeof error === 'string' && error.trim() !== '') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && (error.msg || error.message)) {
            errorMessage = error.msg || error.message;
          }
          toast.error(errorMessage);
        }
        const uploadError = new Error('ATTACHMENT_UPLOAD_ERROR');
        uploadError.isHandled = true;
        throw uploadError;
      }
    }

    return uploadedFiles;
  };

  const uploadPendingAcceptanceCriteriaAttachments = async (pendingFiles = acceptanceCriteriaCommentBoxRef.current?.getAttachments?.() || []) => {
    const editorEl = acceptanceCriteriaCommentBoxRef.current?.getEditorElement?.();
    const filesToUpload = [...pendingFiles];

    if (editorEl && typeof fetch !== 'undefined') {
      const blobImgs = editorEl.querySelectorAll('img[src^="blob:"]');
      for (const img of blobImgs) {
        const src = img.getAttribute('src');
        const alreadyTracked = filesToUpload.some(f => f._previewUrl === src);
        if (!alreadyTracked && src) {
          try {
            const res = await fetch(src);
            if (res.ok) {
              const blob = await res.blob();
              const ext = (blob.type || 'image/png').split('/')[1] || 'png';
              const file = new File([blob], img.alt || `screenshot-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
              file._previewUrl = src;
              file._inlineScreenshotId = img.getAttribute('data-screenshot-id') || img.closest('figure')?.getAttribute('data-screenshot-id');
              file._isInline = true;
              filesToUpload.push(file);
            }
          } catch (e) {
            // Blob URL expired or unreachable
          }
        }
      }
    }

    if (filesToUpload.length === 0) {
      return [];
    }

    const uploadedFiles = [];
    for (const file of filesToUpload) {
      if (file?.key && file?.url) {
        uploadedFiles.push({
          ...file,
          _isInline: Boolean(file._inlineScreenshotId || file._isInline || file._previewUrl)
        });
        continue;
      }

      try {
        const uploadedFile = await dispatch(uploadFile(file)).unwrap();
        const uploadedRecord = {
          ...uploadedFile,
          _inlineScreenshotId: file._inlineScreenshotId,
          _previewUrl: file._previewUrl,
          _isInline: Boolean(file._inlineScreenshotId || file._isInline || file._previewUrl)
        };
        uploadedFiles.push(uploadedRecord);
        if (file._inlineScreenshotId || file._previewUrl) {
          acceptanceCriteriaCommentBoxRef.current?.resolveUploadedScreenshotUrl?.(
            file._inlineScreenshotId || file._previewUrl,
            uploadedFile.url,
            uploadedFile.key
          );
        }
      } catch (error) {
        if (!isStorageQuotaExceededError(error)) {
          let errorMessage = t('taskModal.acceptanceCriteriaAttachmentUploadError', 'Failed to upload acceptance criteria attachment. Please try again.');
          if (typeof error === 'string' && error.trim() !== '') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && (error.msg || error.message)) {
            errorMessage = error.msg || error.message;
          }
          toast.error(errorMessage);
        }
        const uploadError = new Error('ATTACHMENT_UPLOAD_ERROR');
        uploadError.isHandled = true;
        throw uploadError;
      }
    }

    return uploadedFiles;
  };

  const handleEditTaskType = (taskType) => {
    setIsCreatingTaskType(false);
    setNewTaskTypeName('');
    setEditingTaskType({ ...taskType, newName: taskType.name });
  };

  const handleDeleteTaskType = async (taskType) => {
    if (deleteLocksRef.current.taskType) return;
    deleteLocksRef.current.taskType = true;
    setIsDeletingTaskTypeSubmitting(true);
    try {
      await dispatch(deleteTicketDropdown({ id: taskType.id, category: 'type' })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.taskType = false;
      setIsDeletingTaskTypeSubmitting(false);
    }
  };

  const handleEditPriority = (priority) => {
    setIsCreatingPriority(false);
    setNewPriorityName('');
    setEditingPriority({ ...priority, newName: priority.name, newColor: priority.colour || COLORS.primary });
  };

  const handleDeletePriority = async (priority) => {
    if (deleteLocksRef.current.priority) return;
    deleteLocksRef.current.priority = true;
    setIsDeletingPrioritySubmitting(true);
    try {
      await dispatch(deleteTicketDropdown({ id: priority.id, category: 'priority' })).unwrap();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.priority = false;
      setIsDeletingPrioritySubmitting(false);
    }
  };

  const [isCreatingTaskType, setIsCreatingTaskType] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [isCreatingPriority, setIsCreatingPriority] = useState(false);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [newPriorityColor, setNewPriorityColor] = useState(COLORS.primary);
  const [createPriorityAnchor, setCreatePriorityAnchor] = useState(null);
  const [editPriorityAnchor, setEditPriorityAnchor] = useState(null);
  const [editingTaskType, setEditingTaskType] = useState(null);
  const [editingPriority, setEditingPriority] = useState(null);
  const createLocksRef = useRef({
    taskType: false,
    priority: false
  });
  const deleteLocksRef = useRef({
    taskType: false,
    priority: false
  });
  const [isCreatingTaskTypeSubmitting, setIsCreatingTaskTypeSubmitting] = useState(false);
  const [isCreatingPrioritySubmitting, setIsCreatingPrioritySubmitting] = useState(false);
  const [isDeletingTaskTypeSubmitting, setIsDeletingTaskTypeSubmitting] = useState(false);
  const [isDeletingPrioritySubmitting, setIsDeletingPrioritySubmitting] = useState(false);



  const handleCreateTaskTypeConfirm = async () => {
    if (createLocksRef.current.taskType || !newTaskTypeName.trim() || !formData.project) return;

    createLocksRef.current.taskType = true;
    setIsCreatingTaskTypeSubmitting(true);
    try {
      const created = await dispatch(createTicketType({ name: newTaskTypeName, projectId: formData.project })).unwrap();
      if (created?.id) {
        updateFormData((prev) => ({ ...prev, taskType: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_CREATE_FAILED;
      toast.error(errorMessage);
    } finally {
      createLocksRef.current.taskType = false;
      setIsCreatingTaskTypeSubmitting(false);
      setNewTaskTypeName('');
      setIsCreatingTaskType(false);
    }
  };

  const handleCreateTaskTypeCancel = () => {
    setNewTaskTypeName('');
    setIsCreatingTaskType(false);
  };

  const handleCreatePriorityConfirm = async () => {
    if (createLocksRef.current.priority || !newPriorityName.trim() || !formData.project) return;

    createLocksRef.current.priority = true;
    setIsCreatingPrioritySubmitting(true);
    try {
      const created = await dispatch(createTicketPriority({ name: newPriorityName, colour: newPriorityColor, projectId: formData.project })).unwrap();
      if (created?.id) {
        updateFormData((prev) => ({ ...prev, priority: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_CREATE_FAILED;
      toast.error(errorMessage);
    } finally {
      createLocksRef.current.priority = false;
      setIsCreatingPrioritySubmitting(false);
      setNewPriorityName('');
      setNewPriorityColor(COLORS.primary);
      setIsCreatingPriority(false);
    }
  };

  const handleCreatePriorityCancel = () => {
    setNewPriorityName('');
    setNewPriorityColor(COLORS.primary);
    setIsCreatingPriority(false);
  };

  const handleUpdateTaskType = async () => {
    if (editingTaskType?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingTaskType.id, 
          name: editingTaskType.newName, 
          category: 'type',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingTaskType(null);
      }
    }
  };

  const handleUpdatePriority = async () => {
    if (editingPriority?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingPriority.id, 
          name: editingPriority.newName, 
          colour: editingPriority.newColor,
          category: 'priority',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingPriority(null);
      }
    }
  };



  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
    return initials || name[0]?.toUpperCase() || '';
  };

  const getStatusColor = (name) => {
    switch (name) {
      case 'Open': return COLORS.status.open;
      case 'In Progress': return COLORS.status.inProgress;
      case 'Review': return COLORS.status.review;
      case 'Done': return COLORS.status.done;
      case 'Closed': return COLORS.status.closed;
      case 'Blocked': return COLORS.status.blocked;
      default: return stringToColor(name || '');
    }
  };

  const dropdownData = {
    types: dropdowns.types || [],
    priorities: dropdowns.priorities || [],
    statuses: dropdowns.statuses || [],
    projects: projectDropdownsByUser || [],
    assignees: dropdowns.assignees || [],
    labels: dropdowns.labels || [],
    sprints: dropdowns.sprints || []
  };
  
//Adding null check for dropdownData.projects in EditTaskModal
  const selectedProject = (dropdownData.projects || []).find(x => x.id === formData.project);
  const isScrum = (selectedProject?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
  const projectSprintId = isScrum ? (taskData?.scrum_sprint_id || null) : (taskData?.sprint_id || (taskData?.sprint && typeof taskData.sprint === 'object' ? taskData.sprint.id : taskData?.sprint));
  const projectSprintName = isScrum ? (taskData?.scrum_sprint || null) : (taskData?.sprint && typeof taskData.sprint === 'object' ? taskData.sprint.name : null);

  const hasFormChanges = isAddMode || isFormDirty;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!validateTitle(formData.title)) {
      return;
    }

    // Validate dates and time before submitting
    if (!validateDates(formData.startDate, formData.dueDate, formData.startTime, formData.dueTime)) {
      return;
    }
    
    if (!validateTimeFormat(formData.estimateTime)) {
      return;
    }

    if (!isRecurrenceComplete(formData.is_recurring, formData.recurrence_config)) {
      toast.error('Please select both a recurrence frequency and repeat value.');
      return;
    }

    if (isScrum && formData.storyPoints !== '' && formData.storyPoints !== null) {
      if (!validateStoryPoints()) {
        return;
      }
    }

    if (!isScrum && (!formData.assignees || formData.assignees.length === 0)) {
      toast.error(t('taskModal.assigneeRequired', 'Please select at least one assignee'));
      return;
    }

    setIsSubmitting(true);

    try {
      const existingAttachments = formData.attachments.filter(file => file?.key && file?.url);
      const pendingDescriptionAttachments = descriptionCommentBoxRef.current?.getAttachments?.() || [];
      const pendingAcceptanceCriteriaAttachments = acceptanceCriteriaCommentBoxRef.current?.getAttachments?.() || [];
      const pendingNonInlineAttachments = [
        ...filterEmbeddedAttachments(pendingDescriptionAttachments),
        ...filterEmbeddedAttachments(pendingAcceptanceCriteriaAttachments)
      ];
      if (existingAttachments.length + pendingNonInlineAttachments.length > CHARACTER_LIMITS.MAX_ATTACHMENTS) {
        toast.error(t('taskModal.attachmentLimitExceededOnSave'));
        return;
      }

      const descriptionAttachments = await uploadPendingDescriptionAttachments(pendingDescriptionAttachments);
      const acceptanceCriteriaAttachments = await uploadPendingAcceptanceCriteriaAttachments(pendingAcceptanceCriteriaAttachments);
      let currentDescription = descriptionCommentBoxRef.current?.getContent?.() ?? formData.description;
      if (currentDescription && descriptionAttachments.length > 0) {
        descriptionAttachments.forEach(att => {
          if (att._previewUrl && (att.key || att.url)) {
            const canonicalUrl = att.key ? buildCanonicalInlineImageUrl(att.key) : att.url;
            currentDescription = currentDescription.split(att._previewUrl).join(canonicalUrl);
          }
        });
      }

      let currentAcceptanceCriteria = acceptanceCriteriaCommentBoxRef.current?.getContent?.() ?? formData.acceptanceCriteria;
      if (currentAcceptanceCriteria && acceptanceCriteriaAttachments.length > 0) {
        acceptanceCriteriaAttachments.forEach(att => {
          if (att._previewUrl && (att.key || att.url)) {
            const canonicalUrl = att.key ? buildCanonicalInlineImageUrl(att.key) : att.url;
            currentAcceptanceCriteria = currentAcceptanceCriteria.split(att._previewUrl).join(canonicalUrl);
          }
        });
      }

      const nonInlineExistingAttachments = filterEmbeddedAttachments(existingAttachments, currentDescription);
      const nonInlineDescriptionAttachments = filterEmbeddedAttachments(descriptionAttachments, currentDescription);
      const nonInlineAcceptanceCriteriaAttachments = filterEmbeddedAttachments(acceptanceCriteriaAttachments, currentAcceptanceCriteria);
      const savedAttachments = [
        ...nonInlineExistingAttachments,
        ...nonInlineDescriptionAttachments,
        ...nonInlineAcceptanceCriteriaAttachments
      ];

      if (savedAttachments.length > CHARACTER_LIMITS.MAX_ATTACHMENTS) {
        toast.error(t('taskModal.attachmentLimitExceededOnSave'));
        return;
      }

      // Format attachments as array of objects with key and url
      const attachments = savedAttachments.map(file => ({
        key: file.key,
        url: file.url
      }));
      
      const submitData = {
        ...formData,
        startDate: buildDateTimeString(formData.startDate, formData.startTime, { isEditMode: true }),
        dueDate: buildDateTimeString(formData.dueDate, formData.dueTime, { isEditMode: true }),
        has_due_time: Boolean(formData.dueTime),
        description: normalizeRichTextHtml(currentDescription),
        attachments: attachments,
        labels: formData.labels || [],
        estimate_time_in_minutes: !isScrum ? timeStringToMinutes(formData.estimateTime) : null,
        story_points: isScrum && formData.storyPoints ? Number(formData.storyPoints) : null,
        acceptanceCriteria: normalizeRichTextHtml(currentAcceptanceCriteria),
        acceptance_criteria: normalizeRichTextHtml(currentAcceptanceCriteria),
        checklist: buildChecklistPayload(formData.checklist),
        parent_ticket_id: formData.parent_ticket_id || null,
        is_recurring: Boolean(formData.is_recurring),
        recurrence_config: formData.is_recurring ? normalizeRecurrenceConfig(formData.recurrence_config) : null
      };

      if (!isAddMode) {
        // Send explicit deltas so a stale modal snapshot cannot replace newer files.
        const originalAttachments = Array.isArray(originalData?.attachments) ? originalData.attachments : [];
        const originalKeys = new Set(originalAttachments.map(file => file?.key).filter(Boolean));
        const savedKeys = new Set(savedAttachments.map(file => file?.key).filter(Boolean));
        const add = savedAttachments.filter(file => file?.key && !originalKeys.has(file.key));
        const remove_keys = originalAttachments.map(file => file?.key).filter(key => key && !savedKeys.has(key));
        if (add.length || remove_keys.length) {
          submitData.attachment_operations = {
            ...(add.length ? { add } : {}),
            ...(remove_keys.length ? { remove_keys } : {})
          };
        }
        delete submitData.attachments;
      }
      delete submitData.startTime;
      delete submitData.dueTime;

      if (!isAddMode && formData.id && checklistChangesRef.current.length > 0) {
        const latestResponse = await api.get(ENDPOINTS.TICKETS_DETAILS(formData.id));
        const latestChecklist = latestResponse.data?.data?.checklist || [];

        submitData.checklist = checklistChangesRef.current.reduce(
          (mergedChecklist, changeMeta) => mergeChecklistChange(mergedChecklist, formData.checklist, changeMeta),
          latestChecklist
        );
      } else if (!isAddMode) {
        // Do not re-save an unchanged checklist from this modal's initial (and
        // potentially stale) ticket snapshot.
        delete submitData.checklist;
      }
      delete submitData.next_rep_on;
      
      if (isScrum) {
        submitData.scrum_sprint_id = projectSprintId || formData.sprint || null;
        
        if (submitData.scrum_sprint_id && (!submitData.story_points)) {
          toast.error(t('taskModal.storyPointsRequired'));
          return;
        }
        
        delete submitData.sprint;
      } else {
        submitData.sprint = formData.sprint || null;
      }
      
      // Call the onSubmit prop instead of dispatching directly
      if (onSubmit) {
        await onSubmit(submitData);
        descriptionCommentBoxRef.current?.setAttachments?.([]);
        acceptanceCriteriaCommentBoxRef.current?.setAttachments?.([]);
        const updatedFormData = {
          ...formData,
          description: currentDescription,
          acceptanceCriteria: currentAcceptanceCriteria,
          attachments: savedAttachments
        };
        setFormData(prev => ({
          ...prev,
          description: currentDescription,
          acceptanceCriteria: currentAcceptanceCriteria,
          attachments: savedAttachments
        }));
        setOriginalData(updatedFormData);
        setIsFormDirty(false);
        checklistChangesRef.current = [];
        
        // Clear all drafts on successful submit
        const draftId = formData.id || (isAddMode ? 'new' : null);
        if (draftId) {
          clearDraftFields(draftId, ['description', 'acceptanceCriteria']);
        }
      }
      // Don't call onClose() here - let parent handle it
    } catch (error) {
      console.error('Error in EditTaskModal handleSubmit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear drafts on cancel
    const draftId = formData.id || (isAddMode ? 'new' : null);
    if (draftId) {
      clearDraftFields(draftId, ['description', 'acceptanceCriteria']);
    }
    
    onClose();
  };

  if (dropdownsLoading || projectDropdownsByUserLoading) {
    return (
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>{t('taskModal.loadingDropdownData')}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      className="edit-task-modal"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: isAddMode ? COLORS.primary : COLORS.success }}>
            {isAddMode ? <Add /> : <Edit />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">{isAddMode ? t('taskModal.createNewTask', 'Create New Task') : t('taskModal.editTask')}</Typography>
            <Typography variant="body2" color="text.secondary">{isAddMode ? t('taskModal.addNewTask', 'Add a new task to your project') : t('taskModal.updateTaskDetails')}</Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskAlt fontSize="small" /> {t('taskModal.taskInformation')}
          </Typography>
          <TextField
            fullWidth
            label={t('taskModal.taskTitle')}
            name="title"
            value={formData.title}
            onChange={(e) => {
              handleInputChange('title', e.target.value);
            }}
            onPaste={handleTitlePaste}
            placeholder={t('taskModal.enterTaskTitle')}
            required
            variant="outlined"
            sx={{ mb: 3 }}
            inputProps={{ maxLength: CHARACTER_LIMITS.TICKET_TITLE }}
            error={!!titleError}
            helperText={titleError || `${formData.title.length}/${CHARACTER_LIMITS.TICKET_TITLE} chars`}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <DropdownSelector
                value={formData.labels}
                onChange={(labels) => handleInputChange('labels', labels)}
                items={dropdownData.labels}
                projectId={formData.project}
                userRole={userRole}
                category="labels"
                label="taskModal.labels"
                placeholder="taskModal.enterLabelName"
                createText="taskModal.createNewLabel"
                multiple={true}
                withColor={true}
                createOptionPosition="top"
              />
            </Box>
            {(!isScrum || (isScrum && projectSprintId)) && (
              <Box sx={{ flex: 1 }}>
                {isScrum && projectSprintId ? (
                  <TextField
                    fullWidth
                    label={t('taskModal.sprint')}
                    value={projectSprintName || (isScrum ? 'Unnamed Sprint' : projectSprintId)}
                    disabled
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root': { height: '40px', borderRadius: '8px' }
                    }}
                  />
                ) : (
                  <DropdownSelector
                    value={formData.sprint}
                    onChange={(sprint) => handleInputChange('sprint', sprint)}
                    items={dropdownData.sprints}
                    projectId={formData.project}
                    userRole={userRole}
                    category="sprint"
                    label="taskModal.sprint"
                    placeholder="taskModal.enterSprintName"
                    createText="taskModal.createNewSprint"
                    multiple={false}
                    withColor={true}
                    createOptionPosition="top"
                  />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t('taskModal.taskType')}</InputLabel>
              <Select
                value={formData.taskType}
                onChange={(e) => handleInputChange('taskType', e.target.value)}
                onClose={() => {
                  setEditingTaskType(null);
                  setIsCreatingTaskType(false);
                  setNewTaskTypeName('');
                }}
                label={t('taskModal.taskType')}
                renderValue={(selected) => {
                  const item = (dropdownData.types || []).find(x => x.id === selected);
                  if (!item) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '11px', bgcolor: stringToColor(item.name), flexShrink: 0 }}>
                        {getInitials(item.name)}
                      </Avatar>
                      <OverflowTooltip title={item.name} tooltipWidth={300} className="overflow-tooltip-flex" placement="top-start" sx={{ minWidth: 0 }} />
                    </Box>
                  );
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    pr: '32px !important',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      maxWidth: 300,
                      overflowY: 'auto'
                    },
                    onMouseDown: (e) => {
                      if (e.target.closest('.create-option')) {
                        e.preventDefault();
                      }
                    }
                  }
                }}
              >
                {!isCreatingTaskType && (
                  <MenuItem
                    className="create-option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingTaskType(null);
                      setIsCreatingTaskType(true);
                    }}
                    sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                      <Add sx={{ fontSize: 16 }} />
                      <span>{t('taskModal.createNewTaskType')}</span>
                    </Box>
                  </MenuItem>
                )}
                {isCreatingTaskType && (
                  <MenuItem sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                      <TextField
                        size="small"
                        placeholder={t('taskModal.enterTaskTypeName')}
                        value={newTaskTypeName}
                        onChange={(e) => {
                          e.stopPropagation();
                          setNewTaskTypeName(e.target.value);
                        }}
                        inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                        helperText={`${newTaskTypeName.length}/${CHARACTER_LIMITS.TASK_TYPE_NAME} characters`}
                        sx={{ flex: 1 }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateTaskTypeConfirm();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCreateTaskTypeCancel();
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateTaskTypeConfirm();
                        }}
                        disabled={isCreatingTaskTypeSubmitting || !newTaskTypeName.trim() || !formData.project || newTaskTypeName.length > CHARACTER_LIMITS.TASK_TYPE_NAME}
                        sx={{ color: COLORS.success, mt: 0.25 }}
                      >
                        <Check sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateTaskTypeCancel();
                        }}
                        sx={{ color: COLORS.error, mt: 0.25 }}
                      >
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </MenuItem>
                )}
                {dropdownData.types.map((type) => (
                  <MenuItem key={type.id} value={editingTaskType?.id === type.id ? '' : type.id}>
                    {editingTaskType?.id === type.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                        <TextField
                          size="small"
                          value={editingTaskType.newName}
                          onChange={(e) => {
                            e.stopPropagation();
                            setEditingTaskType(prev => ({ ...prev, newName: e.target.value }));
                          }}
                          inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                          helperText={`${editingTaskType.newName.length}/${CHARACTER_LIMITS.TASK_TYPE_NAME} characters`}
                          sx={{ flex: 1 }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleUpdateTaskType();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setEditingTaskType(null);
                            }
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateTaskType(); }} disabled={!editingTaskType.newName.trim() || editingTaskType.newName.length > CHARACTER_LIMITS.TASK_TYPE_NAME} sx={{ color: COLORS.success, mt: 0.25 }}>
                          <Check sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingTaskType(null); }} sx={{ color: COLORS.error, mt: 0.25 }}>
                          <Clear sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '12px', bgcolor: stringToColor(type.name) }}>{getInitials(type.name)}</Avatar>
                          <OverflowTooltip title={type.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                        </Box>
                        {formData.taskType !== type.id && !type.status && canManageTaskTypes && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTaskType(type);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.currentTarget.disabled = true;
                                handleDeleteTaskType(type);
                              }}
                              disabled={isDeletingTaskTypeSubmitting}
                              sx={{ p: 0.25 }}
                            >
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t('taskModal.priority')}</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                onClose={() => {
                  setEditingPriority(null);
                  setIsCreatingPriority(false);
                  setNewPriorityName('');
                }}
                label={t('taskModal.priority')}
                renderValue={(selected) => {
                  const item = (dropdownData.priorities || []).find(x => x.id === selected);
                  if (!item) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '11px', bgcolor: getPriorityColor(item, { priorities: dropdownData.priorities }), flexShrink: 0 }}>
                        {getInitials(item.name)}
                      </Avatar>
                      <OverflowTooltip title={item.name} tooltipWidth={300} className="overflow-tooltip-flex" placement="top-start" sx={{ minWidth: 0 }} />
                    </Box>
                  );
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    pr: '32px !important',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      maxWidth: 300,
                      overflowY: 'auto'
                    },
                    onMouseDown: (e) => {
                      if (e.target.closest('.create-option')) {
                        e.preventDefault();
                      }
                    }
                  }
                }}
              >
                {!isCreatingPriority && (
                  <MenuItem
                    className="create-option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingPriority(null);
                      setIsCreatingPriority(true);
                    }}
                    sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                      <Add sx={{ fontSize: 16 }} />
                      <span>{t('taskModal.createNewPriority')}</span>
                    </Box>
                  </MenuItem>
                )}
                {isCreatingPriority && (
                  <MenuItem sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                      <TextField
                        size="small"
                        placeholder={t('taskModal.enterPriorityName')}
                        value={newPriorityName}
                        onChange={(e) => {
                          e.stopPropagation();
                          setNewPriorityName(e.target.value);
                        }}
                        inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                        helperText={`${newPriorityName.length}/${CHARACTER_LIMITS.PRIORITY_NAME} characters`}
                        sx={{ flex: 1 }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreatePriorityConfirm();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCreatePriorityCancel();
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreatePriorityAnchor(e.currentTarget);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="color-picker-preview"
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: newPriorityColor,
                          
                          mr: 1
                        }}
                      />
                      <CustomColorPopover
                        open={Boolean(createPriorityAnchor)}
                        anchorEl={createPriorityAnchor}
                        onClose={() => setCreatePriorityAnchor(null)}
                        colour={newPriorityColor}
                        onChange={setNewPriorityColor}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreatePriorityConfirm();
                        }}
                        disabled={isCreatingPrioritySubmitting || !newPriorityName.trim() || !formData.project || newPriorityName.length > CHARACTER_LIMITS.PRIORITY_NAME}
                        sx={{ color: COLORS.success, mt: 0.25 }}
                      >
                        <Check sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreatePriorityCancel();
                        }}
                        sx={{ color: COLORS.error, mt: 0.25 }}
                      >
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </MenuItem>
                )}
                {dropdownData.priorities.map((priority) => (
                  <MenuItem key={priority.id} value={editingPriority?.id === priority.id ? '' : priority.id}>
                    {editingPriority?.id === priority.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                        <TextField
                          size="small"
                          value={editingPriority.newName}
                          onChange={(e) => {
                            e.stopPropagation();
                            setEditingPriority(prev => ({ ...prev, newName: e.target.value }));
                          }}
                          inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                          helperText={`${editingPriority.newName.length}/${CHARACTER_LIMITS.PRIORITY_NAME} characters`}
                          sx={{ flex: 1 }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleUpdatePriority();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setEditingPriority(null);
                            }
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditPriorityAnchor(e.currentTarget);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="color-picker-preview"
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: editingPriority.newColor,
                            
                            mr: 0.5,
                            mt: 0.5
                          }}
                        />
                        <CustomColorPopover
                          open={Boolean(editPriorityAnchor)}
                          anchorEl={editPriorityAnchor}
                          onClose={() => setEditPriorityAnchor(null)}
                          colour={editingPriority.newColor}
                          onChange={(color) => setEditingPriority(prev => ({ ...prev, newColor: color }))}
                        />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdatePriority(); }} disabled={!editingPriority.newName.trim() || editingPriority.newName.length > CHARACTER_LIMITS.PRIORITY_NAME} sx={{ color: COLORS.success, mt: 0.25 }}>
                          <Check sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingPriority(null); }} sx={{ color: COLORS.error, mt: 0.25 }}>
                          <Clear sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '12px', bgcolor: getPriorityColor(priority, { priorities: dropdownData.priorities }) }}>{getInitials(priority.name)}</Avatar>
                          <OverflowTooltip title={priority.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                        </Box>
                        {formData.priority !== priority.id && !priority.status && canManagePriorities && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditPriority(priority);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.currentTarget.disabled = true;
                                handleDeletePriority(priority);
                              }}
                              disabled={isDeletingPrioritySubmitting}
                              sx={{ p: 0.25 }}
                            >
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <RecurrencePanel
            isRecurring={formData.is_recurring}
            recurrenceConfig={formData.recurrence_config}
            initialRecurrenceConfig={!isAddMode ? taskData?.recurrence_config : undefined}
            nextRepOn={formData.next_rep_on}
            onChange={({ isRecurring, recurrenceConfig }) => {
              updateFormData((prev) => ({
                ...prev,
                is_recurring: isRecurring,
                recurrence_config: isRecurring ? recurrenceConfig : null
              }));
            }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person fontSize="small" /> {t('taskModal.assignment')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t('taskModal.status')}</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label={t('taskModal.status')}
                renderValue={(selected) => {
                  const item = (dropdownData.statuses || []).find(x => x.id === selected);
                  if (!item) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '11px', bgcolor: getStatusColor(item.name), flexShrink: 0 }}>
                        {getInitials(item.name)}
                      </Avatar>
                      <OverflowTooltip title={item.name} tooltipWidth={300} className="overflow-tooltip-flex" placement="top-start" sx={{ minWidth: 0 }} />
                    </Box>
                  );
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    pr: '32px !important',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 260,
                      maxWidth: 300,
                      overflowY: 'auto'
                    }
                  }
                }}
              >
                {dropdownData.statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '12px', bgcolor: getStatusColor(status.name) }}>{getInitials(status.name)}</Avatar>
                      <OverflowTooltip title={status.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined" sx={{ border: `1px solid rgba(0,0,0,0.23)`, borderRadius: 1, '&:hover': { borderColor: 'rgba(0,0,0,0.87)' } }}>
              <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 0.5 }}>
                {t('taskModal.assignee')}{!isScrum ? '*' : ''}
              </InputLabel>
              <Box sx={{ px: 1.75, pt: 2, pb: 1, minHeight: 56 }}>
                <AssigneeSelector
                  value={formData.assignees}
                  onChange={(ids) => handleInputChange('assignees', ids)}
                  assignees={dropdownData.assignees}
                  variant="chips"
                />
              </Box>
            </FormControl>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment fontSize="small" /> {t('taskModal.projectTimeline')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
              {t('taskModal.project')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                border: `1px solid ${COLORS.gray[300]}`,
                borderRadius: 1,
                bgcolor: COLORS.gray[50]
              }}
            >
              {selectedProject ? (
                <>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '11px', bgcolor: stringToColor(selectedProject.name) }}>
                    {getInitials(selectedProject.name)}
                  </Avatar>
                  <OverflowTooltip title={selectedProject.name} tooltipWidth={500} variant="body2" className="overflow-tooltip-flex" placement="top-start" />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No project selected</Typography>
              )}
            </Box>
          </Box>
          
          <Box className="project-timeline-container">
            <Typography variant="body2" className="project-timeline-label">
              {t('taskModal.projectTimeline')}
            </Typography>
            <CustomDateRangePicker
              startDate={formData.startDate}
              endDate={formData.dueDate}
              startTime={formData.startTime}
              dueTime={formData.dueTime}
              showTime={true}
              onOpen={() => setDateError('')}
              onChange={({ startDate, endDate, startTime, dueTime }) => {
                updateFormData(prev => {
                  const nextData = { ...prev, startDate, dueDate: endDate };
                  if (startTime !== undefined) nextData.startTime = startTime;
                  if (dueTime !== undefined) nextData.dueTime = dueTime;
                  validateDates(nextData.startDate, nextData.dueDate, nextData.startTime, nextData.dueTime);
                  return nextData;
                });
              }}
              placeholder={t('taskModal.selectDates')}
            />
          </Box>
          {dateError && (
            <Typography color="error" sx={{ mt: -2, mb: 3, fontSize: '12px' }}>
              {dateError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              {isScrum ? (
                <>
                  <TextField
                    fullWidth
                    label="Story Points"
                    type="number"
                    inputProps={{ min: 1, max: 40 }}
                    value={formData.storyPoints}
                    onChange={(e) => handleInputChange('storyPoints', e.target.value.replace(REGEX.nonNumeric, ''))}
                    onKeyDown={(e) => { if (RESTRICTED_NUMBER_KEYS.includes(e.key)) e.preventDefault(); }}
                    placeholder="e.g. 3"
                    variant="outlined"
                    error={!!storyPointsError}
                    sx={{
                      '& .MuiFormHelperText-root': { display: 'none' }
                    }}
                  />
                  {storyPointsError && (
                    <Typography color="error" sx={{ fontSize: '0.75rem', lineHeight: 1.66, margin: '3px 14px 0' }}>
                      {storyPointsError}
                    </Typography>
                  )}
                </>
              ) : (
                <TextField
                  fullWidth
                  label="Estimate Time"
                value={formData.estimateTime}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only digits, 'h', 'm', and spaces
                  if (/^[\dhm\s]*$/.test(value)) {
                    const digitError = validateTimeDigitLimits(value);
                    if (digitError) {
                      setTimeError(digitError);
                      return; // Don't update the input if limit exceeded
                    } else {
                      setTimeError('');
                    }
                    handleInputChange(t('taskModal.estimateTime'), value);
                  }
                }}
                onBlur={(e) => {
                  const normalized = normalizeTimeString(e.target.value);
                  if (normalized !== e.target.value) {
                    handleInputChange(t('taskModal.estimateTime'), normalized);
                  }
                }}
                placeholder="e.g., 1h 30m, 2h, 45m"
                variant="outlined"
                error={!!timeError}
                sx={{
                  '& .MuiFormHelperText-root': { display: 'none' }
                }}
              />
              )}
              {timeError && (
                <Typography 
                  color="error" 
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1.66,
                    margin: '3px 14px 0'
                  }}
                >
                  {timeError}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                options={parentTicketOptions}
                loading={isParentTicketLoading}
                openOnFocus
                disableClearable={true}
                filterOptions={(options) => options}
                value={
                  parentTicketOptions.find((ticket) => String(ticket.id) === String(formData.parent_ticket_id)) ||
                  (String(taskData?.parent_ticket?.id) === String(formData.parent_ticket_id) ? taskData.parent_ticket : null) ||
                  null
                }
                onChange={(_, selectedOption) => {
                  handleInputChange('parent_ticket_id', selectedOption?.id || '');
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setParentTicketSearchTerm(value);
                  }
                }}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  if (option.isNone) return t('taskModal.none', 'None');
                  if (typeof option === 'string') return option;
                  const ticketNumber = option.ticket_number || option.number;
                  const ticketTitle = option.title || option.name || option.ticket_title || '';
                  return ticketNumber ? `${ticketNumber} - ${ticketTitle}` : ticketTitle;
                }}
                sx={{ 
                  '& .MuiInputBase-input': { fontSize: '13px' },
                }}
                slotProps={{
                  listbox: {
                    sx: {
                      '& .MuiAutocomplete-option': {
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block'
                      }
                    }
                  },
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-paper': {
                        width: 'fit-content',
                        minWidth: '100px',
                        maxWidth: '150px'
                      }
                    }
                  }
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  const ticketNumber = option.ticket_number || option.number || '';
                  const ticketTitle = option.title || option.name || option.ticket_title || '';
                  const label = option.isNone ? t('taskModal.none', 'None') : (typeof option === 'string' ? option : `${ticketNumber} - ${ticketTitle}`.replace(/^ - /, ''));
                  return (
                    <li key={key} {...optionProps}>
                      <OverflowTooltip title={label} sx={{ fontSize: '13px', width: '100%' }} />
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                noOptionsText={parentTicketSearchTerm ? 'No matching tickets' : 'No tickets available'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('taskModal.subtaskOf', 'Subtask Of')}
                    placeholder={t('taskModal.searchTask', 'Search task')}
                    helperText={t('taskModal.subtaskOfHelper', 'Select parent ticket (optional)')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isParentTicketLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" /> {t('taskModal.additionalDetails')}
          </Typography>
          <Box
            sx={{
              border: `2px dashed ${COLORS.gray[300]}`,
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              mb: 3,
              cursor: isUploadingFiles ? 'not-allowed' : 'pointer',
              opacity: isUploadingFiles ? 0.6 : 1,
              '&:hover': {
                borderColor: isUploadingFiles ? COLORS.gray[300] : COLORS.primary,
                bgcolor: isUploadingFiles ? 'transparent' : COLORS.blue[50]
              }
            }}
            onClick={() => !isUploadingFiles && document.getElementById('file-upload').click()}
          >
            <input
              id="file-upload"
              type="file"
              hidden
              multiple
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.webm,.mkv"
              disabled={isUploadingFiles}
            />
            {isUploadingFiles ? (
              <>
                <CircularProgress size={24} sx={{ mb: 1 }} />
                <Typography variant="body2" sx={{ color: COLORS.gray[500] }}>
                  {t('taskModal.uploadingFiles')}
                </Typography>
              </>
            ) : (
              <>
                <AttachFile sx={{ color: COLORS.gray[500], mb: 1 }} />
                <Typography variant="body2" sx={{ color: COLORS.gray[500] }}>
                  {t('taskModal.clickToUploadFiles')}
                </Typography>
              </>
            )}
          </Box>
          
          {(() => {
            const currentDesc = formData.description || taskData?.description || '';
            const visibleAttachments = filterEmbeddedAttachments(formData.attachments || [], currentDesc);
            if (visibleAttachments.length === 0) return null;
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('taskModal.attachedFiles')} ({visibleAttachments.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {visibleAttachments.map((file, index) => {
                    const fileName = file.name || file.key;
                    const fileUrl = file.url;
                    const isImage = fileName && fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                    
                    return isImage && fileUrl ? (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          border: `1px solid ${COLORS.gray[200]}`,
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={fileUrl}
                          alt={fileName}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeAttachment(file)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'white',
                            boxShadow: 1,
                            '&:hover': { bgcolor: COLORS.gray[100] }
                          }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Chip
                        key={index}
                        label={fileName}
                        onDelete={() => removeAttachment(file)}
                        deleteIcon={<Close />}
                        variant="outlined"
                        size="small"
                        sx={{
                          maxWidth: 200,
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
          
          <Box sx={{ mb: 2 }} className="description-container">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
              {t('taskModal.description')}
            </Typography>
            <Box className="description-content">
              <CommentBox
                ref={descriptionCommentBoxRef}
                initialContent={prepareContentForEditing(formData.description)}
                onSubmit={(content) => handleRichTextChange('description', content)}
                onCancel={() => {}}
                hideActions={true}
                mentionUsers={mentionUsersList}
                ticketId={formData.id || (isAddMode ? 'new' : null)}
                fieldName="description"
                enableAutoSave={true}
                onContentChange={!isAddMode ? (content) => handleRichTextChange('description', content) : null}
                autoFocusEditor={true}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }} className="description-container">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
              {t('taskModal.acceptanceCriteria')}
            </Typography>
            <Box className="description-content">
              <CommentBox
                ref={acceptanceCriteriaCommentBoxRef}
                initialContent={prepareContentForEditing(formData.acceptanceCriteria)}
                onSubmit={(content) => handleRichTextChange('acceptanceCriteria', content)}
                onCancel={() => {}}
                hideAttachments={true}
                hideActions={true}
                ticketId={formData.id || (isAddMode ? 'new' : null)}
                fieldName="acceptanceCriteria"
                enableAutoSave={true}
                onContentChange={!isAddMode ? (content) => handleRichTextChange('acceptanceCriteria', content) : null}
                autoFocusEditor={true}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <ChecklistManager
              value={formData.checklist}
              onChange={handleChecklistChange}
              projectId={formData.project}
              taskId={formData.id}
            />
          </Box>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" size="large" disabled={isSubmitting}>{t('common.cancel')}</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          size="large" 
          disabled={isSubmitting || !formData.title.trim() || !!titleError || !!dateError || !hasFormChanges || !isRecurrenceComplete(formData.is_recurring, formData.recurrence_config)}
          sx={{ bgcolor: COLORS.primary, minWidth: 120 }}
        >
          {isAddMode ? t('taskModal.createTask', 'Create Task') : t('taskModal.updateTask')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskModal;
